import React from 'react';
import { Formik, FormikHelpers } from 'formik';
import { useQueryClient } from '@tanstack/react-query';
import * as Yup from 'yup';
import { Box, Divider, Typography } from '@mui/material';
import { VALIDATION_MOBILE_PHONE } from '@uplift/validations';
import { useIsMobile } from '@uplift/hooks';
import { RewriteUserContactByNameErrorResponse } from '@uplift/api';
import { getFormErrorsFromRewriteUserContactByNameResponse } from '@uplift/utils';
import { AxiosError } from 'axios';
import { CancelButton, MaskedInput, ModalActions, ModalContent, ModalTitle, SubmitButton, useUpdateMobileNumber } from '@uplift/ui-v2';
import { useModalStyles } from './styles';
import { ContactName } from '@uplift/types';

export type EditPhoneFormProps = {
  open: boolean;
  userId: string;
  phone: string;
  contactName: ContactName;
  onClose?: (mobileId: string, success: boolean) => void;
};

type PhoneForm = {
  phone: string;
};

const validationSchema = Yup.object().shape({
  phone: VALIDATION_MOBILE_PHONE,
});


export const EditPhoneForm: React.FC<EditPhoneFormProps> = (props) => {
  const { classes } = useModalStyles();
  const isMobile = useIsMobile();
  const queryClient = useQueryClient();
  const { updateMobileNumber } = useUpdateMobileNumber();

  const initialValues: PhoneForm = {
    phone: props.phone ?? '',
  };

  const onSubmit = async (values: PhoneForm, formikHelpers: FormikHelpers<PhoneForm>) => {
    try {
      const updatedMobile = await updateMobileNumber({ userId: props.userId, name: props.contactName, number: values.phone });

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [ 'user-contacts', ContactName.mobile ] }),
        queryClient.invalidateQueries({ queryKey: [ 'user', props.userId, 'contacts' ] }),
      ]);

      props.onClose?.(updatedMobile.data.id, true);
    } catch(error) {
      const errorObj = getFormErrorsFromRewriteUserContactByNameResponse(
        error as AxiosError<RewriteUserContactByNameErrorResponse>,
        props.contactName
      );

      if (errorObj && errorObj[props.contactName]) {
        formikHelpers.setErrors({ phone: errorObj[props.contactName] });
        return;
      }
    }
  };

  const actions = (
    <>
      <Divider />
      <ModalActions>
        <CancelButton fullWidth={isMobile} onClick={() => props.onClose?.(null, false)} variant={isMobile ? 'outlined' : undefined}>
          Cancel
        </CancelButton>
        <SubmitButton fullWidth={isMobile} color="secondary">
          Update
        </SubmitButton>
      </ModalActions>
    </>
  );

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmit}
      enableReinitialize
      validateOnMount
      validationSchema={validationSchema}
    >
      <>
        {isMobile && (
          <ModalTitle className={classes.mobileTitle}>
            Edit mobile number
          </ModalTitle>
        )}
        <ModalContent noPadding>
          <Box className={classes.modalContent}>
            {!isMobile && (
              <Box my={3}>
                <Typography variant="h4" style={{ whiteSpace: 'nowrap' }}>
                  Enter your mobile number
                </Typography>
              </Box>
            )}
            <MaskedInput
              label="Mobile"
              autoFocus
              name="phone"
              type="phone"
              color="secondary"
            />
            <Box mt={8} />
            {!isMobile && actions}
          </Box>
        </ModalContent>
        {isMobile && actions}
      </>
    </Formik>
  );
};
